import { useState, useEffect, useRef, type DragEvent as ReactDragEvent } from 'react';
import { ShoppingCart, Minus, Plus, Trash2, ChevronUp } from 'lucide-react';
import { Button } from './Button';
import { SelectField } from './TextField';
import { formatMoney } from '@/lib/format';
import { DRAG_MIME } from './ProductCard';
import type { CartLine } from '@/lib/sales';

export type CartPanelVariant = 'sidebar' | 'sheet';

type Props = {
  variant: CartPanelVariant;
  cart: CartLine[];
  monedaCodigo: string;
  monedasOperativas: string[];
  onCurrencyChange: (codigo: string) => void;
  onDecrement: (presentacionId: string) => void;
  onIncrement: (presentacionId: string) => void;
  onRemove: (presentacionId: string) => void;
  onCobrar: () => void;
  cobrarDisabled?: boolean;
  cobrarLabel?: string;
  cobrando?: boolean;
  sesionActiva: boolean;
  maxReachedIds?: Set<string>;
  /** Callback cuando el usuario dropea un ProductCard sobre el carrito. */
  onProductDrop?: (presentacionId: string, productId: string) => void;
};

// Note: onDecrement/onIncrement accept a single id; deltas are implied by the
// caller. This matches the ProductCard stepper API.

export function CartPanel({
  variant,
  cart,
  monedaCodigo,
  monedasOperativas,
  onCurrencyChange,
  onDecrement,
  onIncrement,
  onRemove,
  onCobrar,
  cobrarDisabled,
  cobrarLabel,
  cobrando,
  sesionActiva,
  maxReachedIds,
  onProductDrop,
}: Props) {
  const subtotal = cart.reduce((acc, l) => acc + l.subtotal, 0);
  const itemCount = cart.reduce((acc, l) => acc + l.cantidad, 0);

  return (
    <CartBody
      variant={variant}
      cart={cart}
      monedaCodigo={monedaCodigo}
      monedasOperativas={monedasOperativas}
      onCurrencyChange={onCurrencyChange}
      onDecrement={onDecrement}
      onIncrement={onIncrement}
      onRemove={onRemove}
      onCobrar={onCobrar}
      cobrarDisabled={cobrarDisabled}
      cobrarLabel={cobrarLabel}
      cobrando={cobrando}
      sesionActiva={sesionActiva}
      maxReachedIds={maxReachedIds}
      subtotal={subtotal}
      itemCount={itemCount}
      onProductDrop={onProductDrop}
    />
  );
}

type BodyProps = Omit<Props, 'cart'> & {
  cart: CartLine[];
  subtotal: number;
  itemCount: number;
};

function CartBody({
  variant,
  cart,
  monedaCodigo,
  monedasOperativas,
  onCurrencyChange,
  onDecrement,
  onIncrement,
  onRemove,
  onCobrar,
  cobrarDisabled,
  cobrarLabel,
  cobrando,
  sesionActiva,
  maxReachedIds,
  subtotal,
  itemCount,
  onProductDrop,
}: BodyProps) {
  // Bottom-sheet state lives only in 'sheet' variant.
  const [expanded, setExpanded] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: ReactDragEvent) => {
    if (e.dataTransfer.types.includes(DRAG_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: ReactDragEvent) => {
    // Solo limpiar cuando sale del cart-panel completo, no de un hijo
    if (e.currentTarget === e.target) setIsDragOver(false);
  };

  const handleDrop = (e: ReactDragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw || !onProductDrop) return;
    try {
      const payload = JSON.parse(raw) as { id: string; presentacion_id: string };
      onProductDrop(payload.presentacion_id, payload.id);
    } catch {
      // payload invalido; ignorar silenciosamente
    }
  };

  if (variant === 'sidebar') {
    return (
      <div
        onDragOver={onProductDrop ? handleDragOver : undefined}
        onDragLeave={onProductDrop ? handleDragLeave : undefined}
        onDrop={onProductDrop ? handleDrop : undefined}
        className={
          'bg-neutral-0 flex min-h-0 flex-col overflow-hidden rounded-md border transition-colors lg:col-span-5 ' +
          (isDragOver
            ? 'border-primary bg-primary-soft/30 border-2 border-dashed'
            : 'border-neutral-200')
        }
        aria-label="Carrito de compras — dropea productos aquí para agregarlos"
      >
        <SidebarHeader
          monedaCodigo={monedaCodigo}
          monedasOperativas={monedasOperativas}
          onCurrencyChange={onCurrencyChange}
        />
        <CartLines
          cart={cart}
          monedaCodigo={monedaCodigo}
          onDecrement={onDecrement}
          onIncrement={onIncrement}
          onRemove={onRemove}
          maxReachedIds={maxReachedIds}
        />
        <CartFooter
          subtotal={subtotal}
          itemCount={itemCount}
          monedaCodigo={monedaCodigo}
          onCobrar={onCobrar}
          cobrarDisabled={cobrarDisabled}
          cobrarLabel={cobrarLabel}
          cobrando={cobrando}
          sesionActiva={sesionActiva}
          cartIsEmpty={cart.length === 0}
        />
      </div>
    );
  }

  // variant === 'sheet'
  const totalItems = cart.reduce((acc, l) => acc + l.cantidad, 0);
  return (
    <BottomSheet
      expanded={expanded}
      onExpandToggle={() => setExpanded((v) => !v)}
      totalLabel={cart.length > 0 ? formatMoney(subtotal, monedaCodigo) : undefined}
      itemCount={totalItems}
      headerCurrency={
        <SheetCurrencySelect
          monedaCodigo={monedaCodigo}
          monedasOperativas={monedasOperativas}
          onCurrencyChange={onCurrencyChange}
        />
      }
    >
      <CartLines
        cart={cart}
        monedaCodigo={monedaCodigo}
        onDecrement={onDecrement}
        onIncrement={onIncrement}
        onRemove={onRemove}
        maxReachedIds={maxReachedIds}
      />
      <CartFooter
        subtotal={subtotal}
        itemCount={itemCount}
        monedaCodigo={monedaCodigo}
        onCobrar={() => {
          setExpanded(false);
          onCobrar();
        }}
        cobrarDisabled={cobrarDisabled}
        cobrarLabel={cobrarLabel}
        cobrando={cobrando}
        sesionActiva={sesionActiva}
        cartIsEmpty={cart.length === 0}
      />
    </BottomSheet>
  );
}

function SheetCurrencySelect({
  monedaCodigo,
  monedasOperativas,
  onCurrencyChange,
}: {
  monedaCodigo: string;
  monedasOperativas: string[];
  onCurrencyChange: (c: string) => void;
}) {
  return (
    <SelectField
      label="Moneda del carrito"
      hideLabel
      value={monedaCodigo}
      onChange={(e) => onCurrencyChange(e.target.value)}
      className="h-8 w-auto min-w-[72px] py-1 text-xs"
    >
      {(monedasOperativas.length > 0 ? monedasOperativas : ['COP', 'USD', 'VES']).map((m) => (
        <option key={m} value={m}>
          {m}
        </option>
      ))}
    </SelectField>
  );
}

function SidebarHeader({
  monedaCodigo,
  monedasOperativas,
  onCurrencyChange,
}: {
  monedaCodigo: string;
  monedasOperativas: string[];
  onCurrencyChange: (codigo: string) => void;
}) {
  return (
    <div className="shrink-0 border-b border-neutral-200 p-4 pb-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-title flex items-center gap-2 font-semibold text-neutral-900">
          <ShoppingCart className="text-primary h-5 w-5" aria-hidden="true" /> Carrito
        </h2>
        <SelectField
          label="Moneda del carrito"
          hideLabel
          value={monedaCodigo}
          onChange={(e) => onCurrencyChange(e.target.value)}
          className="h-9 w-auto min-w-[88px] py-1 text-xs"
        >
          {(monedasOperativas.length > 0 ? monedasOperativas : ['COP', 'USD', 'VES']).map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </SelectField>
      </div>
    </div>
  );
}

function CartLines({
  cart,
  monedaCodigo,
  onDecrement,
  onIncrement,
  onRemove,
  maxReachedIds,
}: {
  cart: CartLine[];
  monedaCodigo: string;
  onDecrement: (id: string) => void;
  onIncrement: (id: string) => void;
  onRemove: (id: string) => void;
  maxReachedIds?: Set<string>;
}) {
  if (cart.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-10 text-center text-sm text-neutral-500">
        <ShoppingCart className="h-8 w-8 text-neutral-300" aria-hidden="true" />
        <p>Carrito vacío. Seleccioná productos del catálogo.</p>
      </div>
    );
  }
  return (
    <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
      {cart.map((line) => (
        <div
          key={line.presentacionId}
          className="animate-fade-in bg-neutral-0 flex items-center justify-between gap-2 rounded-md border border-neutral-200 p-3"
        >
          <div className="min-w-0 flex-1 pr-2">
            <p className="truncate text-sm font-medium text-neutral-900">{line.productoNombre}</p>
            <p className="truncate text-xs text-neutral-500">
              {line.presentacionNombre} ·{' '}
              <span className="tnum">{formatMoney(line.precioUnitario, monedaCodigo)}</span>
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="bg-neutral-0 flex items-center rounded-sm border border-neutral-200">
              <button
                type="button"
                onClick={() => onDecrement(line.presentacionId)}
                className="flex h-11 w-11 items-center justify-center text-neutral-600 transition-colors hover:bg-neutral-100"
                aria-label={`Restar ${line.productoNombre}`}
              >
                <Minus className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <span
                className="tnum w-7 text-center text-sm font-semibold"
                aria-live="polite"
                aria-atomic="true"
              >
                {line.cantidad}
              </span>
              <button
                type="button"
                onClick={() => onIncrement(line.presentacionId)}
                disabled={maxReachedIds?.has(line.presentacionId)}
                title={
                  maxReachedIds?.has(line.presentacionId)
                    ? 'Máximo disponible alcanzado'
                    : `Sumar ${line.productoNombre}`
                }
                className="flex h-11 w-11 items-center justify-center text-neutral-600 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-300 disabled:hover:bg-transparent"
                aria-label={
                  maxReachedIds?.has(line.presentacionId)
                    ? `Máximo disponible alcanzado para ${line.productoNombre}`
                    : `Sumar ${line.productoNombre}`
                }
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>

            <span className="tnum w-20 text-right text-sm font-semibold text-neutral-900">
              {formatMoney(line.subtotal, monedaCodigo)}
            </span>

            <button
              type="button"
              onClick={() => onRemove(line.presentacionId)}
              className="text-danger hover:bg-danger-soft flex h-11 w-11 items-center justify-center rounded-sm transition-colors"
              aria-label={`Quitar ${line.productoNombre}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CartFooter({
  subtotal,
  itemCount,
  monedaCodigo,
  onCobrar,
  cobrarDisabled,
  cobrarLabel,
  cobrando,
  sesionActiva,
  cartIsEmpty,
}: {
  subtotal: number;
  itemCount: number;
  monedaCodigo: string;
  onCobrar: () => void;
  cobrarDisabled?: boolean;
  cobrarLabel?: string;
  cobrando?: boolean;
  sesionActiva: boolean;
  cartIsEmpty: boolean;
}) {
  return (
    <div className="bg-neutral-0 shrink-0 space-y-2 border-t border-neutral-200 p-4 pt-3">
      {itemCount > 0 && (
        <div className="flex justify-between text-xs text-neutral-600">
          <span>Items</span>
          <span className="tnum">{itemCount}</span>
        </div>
      )}
      <div className="flex justify-between text-xs text-neutral-600">
        <span>Subtotal</span>
        <span className="tnum">{formatMoney(subtotal, monedaCodigo)}</span>
      </div>
      <div className="flex items-baseline justify-between border-t border-neutral-200 pt-2">
        <span className="text-sm font-semibold tracking-wider text-neutral-700 uppercase">
          Total
        </span>
        <span className="tnum text-display font-mono font-bold text-neutral-900">
          {formatMoney(subtotal, monedaCodigo)}
        </span>
      </div>
      {!cartIsEmpty && (
        <Button
          variant="primary"
          fullWidth
          onClick={onCobrar}
          loading={cobrando}
          loadingLabel="Registrando…"
          disabled={cobrarDisabled}
          title="Cobrar (Ctrl+Enter)"
        >
          {!sesionActiva ? 'Abrir caja y cobrar' : (cobrarLabel ?? 'Cobrar')}
          <span className="kbd-inline">
            <kbd>Enter</kbd>
          </span>
        </Button>
      )}
    </div>
  );
}

function BottomSheet({
  expanded,
  onExpandToggle,
  totalLabel,
  itemCount,
  headerCurrency,
  children,
}: {
  expanded: boolean;
  onExpandToggle: () => void;
  totalLabel?: string;
  itemCount: number;
  headerCurrency?: React.ReactNode;
  children: React.ReactNode;
}) {
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExpandToggle();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded, onExpandToggle]);

  return (
    <>
      {expanded && (
        <button
          type="button"
          aria-label="Cerrar carrito"
          onClick={onExpandToggle}
          className="backdrop fixed inset-0 z-30 bg-[rgba(15,23,42,0.4)]"
        />
      )}
      <div
        ref={sheetRef}
        role="region"
        aria-label="Carrito de compras"
        aria-expanded={expanded}
        className={
          'bg-neutral-0 fixed inset-x-0 bottom-0 z-40 flex flex-col rounded-t-lg border-t border-neutral-200 shadow-[var(--shadow-ambient-high)] transition-[max-height] duration-200 ease-out lg:hidden ' +
          (expanded ? 'max-h-[85vh]' : 'max-h-20')
        }
      >
        <button
          type="button"
          onClick={onExpandToggle}
          aria-label={expanded ? 'Colapsar carrito' : 'Expandir carrito'}
          aria-expanded={expanded}
          className="flex h-12 w-full shrink-0 items-center justify-between gap-3 rounded-t-lg px-4 hover:bg-neutral-50"
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-semibold text-neutral-900">
            <ShoppingCart className="text-primary h-4 w-4" aria-hidden="true" />
            Carrito
            {itemCount > 0 && (
              <span className="tnum rounded-pill bg-primary-soft text-primary px-2 py-0.5 text-xs font-semibold">
                {itemCount}
              </span>
            )}
          </span>
          {totalLabel && !expanded && (
            <span className="tnum text-sm font-semibold text-neutral-900">{totalLabel}</span>
          )}
          {headerCurrency && !expanded && headerCurrency}
          <ChevronUp
            className={
              'h-4 w-4 shrink-0 text-neutral-500 transition-transform ' +
              (expanded ? 'rotate-180' : '')
            }
            aria-hidden="true"
          />
        </button>
        {expanded && <div className="flex min-h-0 flex-1 flex-col">{children}</div>}
      </div>
    </>
  );
}
